import { CustomIcon } from "components/icon/customIcon.component";
import { Text } from "components/Typography/text.styled";
import { Color } from "config/colors";
import { FontFamily, FontSize } from "config/font";
import {
  ApplicationDto,
  ApplicationResolution,
  ApplicationStatus,
} from "model/Application";
import { CompanyGig } from "model/Gig";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Api } from "services/api/api.service";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import { getWorkerImages } from "web-apps/company/utils/utils";
import { ApplicantDetailsModal } from "../../applicantDetails/applicantDetailsModal.component";
import {
  DropDownRow,
  ImageContainer,
  ImageWrapper,
  WorkerContainer,
} from "./gigDetail.styled";
import { OutlineCircle, SmallImage } from "../gigList/gigs.styled";

type Props = {
  gigData: CompanyGig;
};

export const GigDetailWorkers: React.FC<Props> = ({ gigData }) => {
  const { t } = useTranslation();
  const [workerImages, setWorkerImages] = useState<string[]>([]);
  const [offeredWorkerImages, setOfferedWorkerImages] = useState<string[]>([]);
  const [workersOpen, setWorkersOpen] = useState<boolean>(true);
  const [workerIds, setWorkerIds] = useState<(number | null)[]>([]);
  const [offers, setOffers] = useState<ApplicationDto[]>();
  const [accepted, setAccepted] = useState<ApplicationDto[]>();
  const [gigApplications, setGigApplications] = useState<ApplicationDto[]>();
  const [companyState] = useCompanyStore();
  const [applicationModalOpen, setApplicationModalOpen] = useState(false);
  const lastShiftEndTime = gigData.shifts[gigData.shifts.length - 1]?.endTime;
  const isShiftOngoing = new Date(lastShiftEndTime).getTime() > Date.now();
  const [hoverOffer, setHoverOffer] = useState(false);
  const [application, setApplication] = useState<ApplicationDto | null>();

  useEffect(() => {
    if (!gigApplications) return;

    const workerFirebaseIds = gigApplications.map(
      (application) => application.worker.externalId
    );
    const workerIds = gigApplications.map(
      (application) => application.worker.id
    );
    const workerImageUrls = Array(workerIds.length).fill(null);

    getWorkerImages(workerFirebaseIds).then((data) => {
      workerImageUrls.splice(0, data.length, ...data);
      setWorkerImages(workerImageUrls);
      setWorkerIds(workerIds);
    });
  }, [gigApplications]);

  useEffect(() => {
    setApplicationModalOpen(true);
  }, [application]);

  const fetchGigApplications = () => {
    if (companyState.company?.id) {
      Api()
        .company.application.getGigApplications(
          companyState.company.id,
          gigData.id
        )
        .then((res) => {
          setGigApplications(res.data);
        })
        .catch((err) => {
          console.log("Error fetching gig applications", err);
        });
    }
  };

  useEffect(() => {
    fetchGigApplications();
  }, []);

  useEffect(() => {
    let offered: ApplicationDto[] = [];
    let accepted: ApplicationDto[] = [];
    if (gigApplications) {
      gigApplications.forEach((application) => {
        if (application.status === ApplicationStatus.OFFERED) {
          offered.push(application);
        }
        if (application.resolution === ApplicationResolution.ACCEPTED) {
          accepted.push(application);
        }
      });
      setOffers(offered);
      setAccepted(accepted);
    }
  }, [gigApplications]);

  useEffect(() => {
    if (!offers) return;

    const workerFirebaseIds = offers.map((offer) => offer.worker.firebaseId);

    const offerIds = offers.map((offer) => offer.worker.id);
    const workerImageUrls = Array(
      offers && offerIds.length + workerIds.length > gigData.amountOfWorkers
        ? offerIds.length
        : gigData.amountOfWorkers - workerIds.length
    ).fill(null);

    getWorkerImages(workerFirebaseIds).then((data) => {
      workerImageUrls.splice(0, data.length, ...data);
      setOfferedWorkerImages(workerImageUrls);
    });
  }, [offers, workerIds]);

  return (
    <>
      <DropDownRow
        onClick={() => {
          setWorkersOpen(!workersOpen);
        }}
      >
        <Text
          fontFamily={FontFamily.MontserratRegular}
          fontSize={FontSize.Large}
        >
          {`${t("GigsCompany.Worker")} (${workerIds.length} / ${
            gigData.amountOfWorkers
          } st)`}
        </Text>
        <CustomIcon
          name={workersOpen ? "upward_arrow" : "downward_arrow"}
          size="24px"
          color={Color.BurntSienna}
          style={{ marginTop: 10 }}
        />
      </DropDownRow>

      {workersOpen && (
        <>
          <WorkerContainer>
            <ImageContainer>
              {hoverOffer && (
                <Text
                  fontSize={FontSize.Small}
                  color={Color.Black}
                  style={{
                    marginLeft: 4,
                    backgroundColor: Color.LightGrey03,
                    padding: 4,
                    borderRadius: 4,
                    marginRight: 4,
                  }}
                >
                  {t("GigsCompany.SendOffer")}
                </Text>
              )}
              {workerImages.map((url, index) => {
                const handleClick = () => {
                  const application = accepted ? accepted[index] : undefined;

                  setApplication(application);
                };
                return (
                  <ImageWrapper key={index}>
                    {url && url !== "no-image" ? (
                      <SmallImage
                        src={url}
                        style={{
                          cursor: "pointer",
                          border: `2px solid ${Color.LighterPositive}`,
                        }}
                        onClick={handleClick}
                      />
                    ) : (
                      <OutlineCircle accepted>
                        <CustomIcon
                          name="person"
                          size="34px"
                          color={Color.SeaBlue500}
                          style={{
                            cursor: isShiftOngoing ? "pointer" : "auto",
                            margin: "0 auto",
                            padding: 4,
                            marginTop: 2,
                          }}
                          onClick={handleClick}
                        />
                      </OutlineCircle>
                    )}
                  </ImageWrapper>
                );
              })}
              {offeredWorkerImages.map((url, index) => {
                const handleClick = () => {
                  const application = offers ? offers[index] : undefined;
                  setApplication(application);
                };
                return (
                  <ImageWrapper key={index}>
                    {url && url !== "no-image" ? (
                      <SmallImage
                        src={url}
                        style={{
                          cursor: "pointer",
                          border: "2px solid orange",
                        }}
                        onClick={handleClick}
                      />
                    ) : url && url == "no-image" ? (
                      <OutlineCircle>
                        <CustomIcon
                          name="person"
                          size="34px"
                          color={Color.SeaBlue500}
                          style={{
                            cursor: "pointer",
                            margin: "0 auto",
                            padding: 4,
                            marginTop: 2,
                          }}
                          onClick={handleClick}
                        />
                      </OutlineCircle>
                    ) : (
                      <CustomIcon
                        name="person"
                        size="34px"
                        color={Color.SeaBlue500}
                        style={{
                          margin: "0 auto",
                          padding: 4,
                          marginTop: 2,
                        }}
                      />
                    )}
                  </ImageWrapper>
                );
              })}
            </ImageContainer>
          </WorkerContainer>
        </>
      )}
      {applicationModalOpen && application && (
        <ApplicantDetailsModal
          applicant={application}
          setWorkerApplication={setApplication}
          setShowApplicationModal={setApplicationModalOpen}
          showApplicationModal={applicationModalOpen}
        />
      )}
    </>
  );
};
